import type { FC, ReactNode } from 'react'

import { Reveal } from '@/components/generals/reveal'
import { RuleDraw } from '@/components/generals/rule-draw'
import { ImagePlate } from '@/components/generals/image-plate'
import { LocalTime } from '@/components/contact/local-time'

import { EASE_CLASS, MICRO_FAINT } from '@/constants/type'
import { studio } from '@/constants/site'

const CHANNELS = [
    {
        label: 'Email',
        value: studio.email,
        href: `mailto:${studio.email}`,
        meta: 'Direct',
        external: false
    },
    {
        label: 'Instagram',
        value: studio.instagram,
        href: studio.instagramUrl,
        meta: 'Daily-ish',
        external: true
    },
    {
        label: 'Telephone',
        value: studio.telephone,
        href: `tel:${studio.telephone.replace(/[^+\d]/g, '')}`,
        meta: 'Mon to Thu',
        external: false
    }
]

export const Channels: FC = (): ReactNode => {
    return (
        <div>
            <Reveal>
                <p className={MICRO_FAINT}>Elsewhere</p>
            </Reveal>

            <ul className='mt-8'>
                {CHANNELS.map((channel, index) => (
                    <li key={channel.label}>
                        <RuleDraw delay={index * 0.06} />

                        <Reveal delay={index * 0.06}>
                            <a
                                href={channel.href}
                                target={channel.external ? '_blank' : undefined}
                                rel={channel.external ? 'noreferrer' : undefined}
                                className='group flex items-center justify-between gap-6 py-5 outline-none'
                            >
                                <span
                                    className='relative block overflow-hidden font-serif tracking-[-0.01em] text-fz-ink'
                                    style={{
                                        fontSize: 'clamp(1.375rem, 2.2vw, 1.75rem)',
                                        lineHeight: 1.2,
                                        height: '1.2em'
                                    }}
                                >
                                    <span
                                        className={`block transition-transform duration-500 group-hover:-translate-y-full group-focus-visible:-translate-y-full ${EASE_CLASS}`}
                                    >
                                        {channel.label}
                                    </span>

                                    <span
                                        className={`block whitespace-nowrap transition-transform duration-500 group-hover:-translate-y-full group-focus-visible:-translate-y-full ${EASE_CLASS}`}
                                    >
                                        {channel.value}
                                    </span>
                                </span>

                                <span
                                    className={`shrink-0 transition-colors duration-500 group-hover:text-fz-sub ${MICRO_FAINT}`}
                                >
                                    {channel.meta}
                                </span>
                            </a>
                        </Reveal>
                    </li>
                ))}

                <li aria-hidden='true'>
                    <RuleDraw delay={0.18} />
                </li>
            </ul>

            <div className='mt-8 flex flex-col gap-8'>
                <Reveal delay={0.06}>
                    <p className={MICRO_FAINT}>Studio</p>

                    <p className='mt-3 max-w-[24ch] text-[15px] leading-relaxed text-fz-sub'>
                        {studio.address}
                    </p>
                </Reveal>

                <Reveal delay={0.12}>
                    <p className={MICRO_FAINT}>{studio.timeCity}</p>

                    <p className='mt-3 text-[15px] leading-relaxed text-fz-sub'>
                        <LocalTime /> — usually at a desk
                    </p>
                </Reveal>

                <Reveal delay={0.18}>
                    <p className={MICRO_FAINT}>New work</p>

                    <p className='mt-3 max-w-[26ch] text-[15px] leading-relaxed text-fz-sub'>
                        We are booking from March. Two slots left this year, one of them for type.
                    </p>
                </Reveal>

                <ImagePlate
                    className='max-w-[16rem]'
                    src='/images/contact-aside.jpg'
                    alt='Portrait held at the edge of a red doorway'
                    ratio='1 / 1'
                    focus='50% 45%'
                    drift={-26}
                    caption='Verso — night sitting'
                />
            </div>
        </div>
    )
}
